import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';

const PatientHeader: React.FC = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Close menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/');
            setIsMenuOpen(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // Patient-specific navigation items - main nav only (reduced for mobile)
    const mainNavItems = [
        { path: '/dashboard', label: 'Dashboard', icon: '📊' },
        { path: '/chat', label: 'Chat', icon: '💬' },
        { path: '/patient-portal', label: 'Portal', icon: '🏠' },
    ];

    // Additional items in dropdown
    const dropdownItems = [
        { path: '/memory', label: 'Memory', icon: '🧠' },
        { path: '/radar', label: 'Radar', icon: '📊' },
        { path: '/profile', label: 'Profile', icon: '👤' },
        { path: '/wellness', label: 'Wellness', icon: '🌱' },
        { path: '/mood-tracker', label: 'Mood', icon: '😊' },
        { path: '/patient-settings', label: 'Settings', icon: '⚙️' },
    ];

    return (
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg">
            <div className="max-w-[768px] md:max-w-[1200px] mx-auto px-4">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-2">
                        <span role="img" aria-label="brain" className="text-2xl">🧠</span>
                        <span className="text-xl font-bold text-white">Mentra</span>
                    </Link>

                        {/* Desktop Navigation */}
                        {user && (
                            <div className="hidden md:flex items-center">
                                <nav className="flex space-x-4 mr-4">
                                    {mainNavItems.map(({ path, label, icon }) => (
                                        <Link
                                            key={path}
                                            to={path}
                                            className={`
                                                flex items-center px-3 py-2 rounded-md transition-colors
                                                ${location.pathname === path
                                                    ? 'bg-purple-500 text-white'
                                                    : 'text-white hover:bg-purple-500'
                                                }
                                            `}
                                        >
                                            <span className="mr-2">{icon}</span>
                                            <span>{label}</span>
                                        </Link>
                                    ))}
                                    
                                    {/* More Tools Dropdown */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                                            className={`
                                                flex items-center px-3 py-2 rounded-md transition-colors
                                                ${dropdownItems.some(item => location.pathname === item.path)
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-purple-600/80 text-white hover:bg-purple-500'
                                                }
                                            `}
                                        >
                                            <span className="mr-2">⚙️</span>
                                            <span>More</span>
                                            <span className="ml-1">▼</span>
                                        </button>
                                        
                                        {/* Dropdown Menu */}
                                        {isMenuOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                                                {dropdownItems.map(({ path, label, icon }) => (
                                                    <Link
                                                        key={path}
                                                        to={path}
                                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="mr-3">{icon}</span>
                                                        <span>{label}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </nav>
                                <div className="flex items-center space-x-4">
                                    <span className="text-sm text-purple-200">Patient View</span>
                                    <button
                                        onClick={handleSignOut}
                                        className="flex items-center px-3 py-1.5 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                                    >
                                        <span className="mr-1.5">👋</span>
                                        <span>Sign Out</span>
                                    </button>
                                </div>
                            </div>
                        )}

                    {/* Mobile Menu Button */}
                    {user && (
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            aria-label="Toggle menu"
                        >
                            <div className={`w-6 h-0.5 mb-1.5 transition-all bg-white ${isMenuOpen ? 'transform rotate-45 translate-y-2' : ''}`}></div>
                            <div className={`w-6 h-0.5 mb-1.5 transition-all bg-white ${isMenuOpen ? 'opacity-0' : ''}`}></div>
                            <div className={`w-6 h-0.5 transition-all bg-white ${isMenuOpen ? 'transform -rotate-45 -translate-y-2' : ''}`}></div>
                        </button>
                    )}
                </div>

                {/* Mobile Navigation */}
                {user && (
                    <div
                        className={`
                            md:hidden transition-all duration-300 ease-in-out overflow-hidden
                            ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                        `}
                    >
                        <nav className="py-4 space-y-2">
                            {[...mainNavItems, ...dropdownItems].map(({ path, label, icon }) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className={`
                                        flex items-center px-3 py-2 rounded-md transition-colors w-full
                                        ${location.pathname === path
                                            ? 'bg-purple-500 text-white'
                                            : 'text-white hover:bg-purple-500'
                                        }
                                    `}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <span className="mr-2">{icon}</span>
                                    <span>{label}</span>
                                </Link>
                            ))}
                            <div className="flex items-center justify-between pt-4 border-t border-purple-400">
                                <button
                                    onClick={handleSignOut}
                                    className="flex items-center px-3 py-1.5 text-sm rounded-md transition-colors bg-purple-600 text-white hover:bg-purple-700"
                                >
                                    <span className="mr-1.5">👋</span>
                                    <span>Sign Out</span>
                                </button>
                            </div>
                        </nav>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientHeader;
